import { useState, useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import { Button } from '@/components/ui/Button'
import { ArrowLeft, Send, Pencil, CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'
import type { MultiUploadState } from '@/hooks/useMultiUpload'
import { submitPhoto, uploadPhotoToS3 } from '@/lib/api'
import { useNavigate } from 'react-router-dom'

interface ReviewStepProps {
  formData: FormData
  onBack: () => void
  goToStep: (step: number) => void
  uploadState?: MultiUploadState
  onRetryPhotoUpload?: () => void
}

export function ReviewStep({ formData, onBack, goToStep, uploadState, onRetryPhotoUpload }: ReviewStepProps) {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Track if we're waiting for uploads to complete before submitting
  const pendingSubmitRef = useRef(false)

  // Get the photo upload state
  const photoUpload = uploadState?.uploads['photo']

  // Check if all uploads are done (no uploading state)
  const hasAnyUploading = uploadState?.isAnyUploading ?? false
  const hasAnyError = uploadState?.isAnyError ?? false

  // Check if upload completed while we were waiting
  useEffect(() => {
    if (pendingSubmitRef.current && !hasAnyUploading && !hasAnyError) {
      pendingSubmitRef.current = false
      handleSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when upload status settles; handleSubmit is re-created each render and including it would loop
  }, [hasAnyUploading, hasAnyError])

  const handleSubmit = async () => {
    // Need either a File to upload OR an already-uploaded S3 key (from draft restore)
    const hasPhotoFile = !!formData.photo
    const hasPhotoS3Key = !!formData.photoS3Key

    if (!hasPhotoFile && !hasPhotoS3Key) return

    // If any upload is still in progress, wait for it
    if (hasAnyUploading) {
      pendingSubmitRef.current = true
      setSubmitStatus('Finishing uploads...')
      setIsSubmitting(true)
      return
    }

    // If any upload failed, show error and prompt retry
    if (hasAnyError) {
      setError('Photo upload failed. Please retry the upload before submitting.')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)
    setError(null)

    try {
      // Use pre-uploaded s3Key if available (from optimistic upload OR draft restore)
      const photoS3Key =
        formData.photoS3Key || (photoUpload?.status === 'success' ? (photoUpload.s3Key ?? undefined) : undefined)

      // Determine content type - from file if available, otherwise infer from S3 key
      const contentType = formData.photo?.type || 'image/jpeg'

      // Step 1: Create submission (with optional pre-uploaded key)
      const { data, error: apiError } = await submitPhoto({
        email: formData.email.trim().toLowerCase(),
        name: formData.name.trim(),
        grade: formData.grade,
        photoTitle: formData.photoTitle.trim(),
        contentType,
        guidelinesAcceptedAt: formData.guidelinesAcceptedAt ?? undefined,
        includesPeople: formData.includesPeople ?? undefined,
        hasModelPermission: formData.hasModelPermission ?? undefined,
        photoS3Key,
      })

      if (apiError || !data) {
        throw new Error(apiError || 'Failed to create submission')
      }

      // Step 2: Upload photo directly to S3 (skip if already uploaded or from draft)
      if (!photoS3Key && data.uploadUrl && formData.photo) {
        setSubmitStatus('Uploading photo...')
        const uploadSuccess = await uploadPhotoToS3(data.uploadUrl, formData.photo)
        if (!uploadSuccess) {
          throw new Error('Failed to upload photo. Please try again.')
        }
      }

      // Success - navigate to success page
      navigate('/submit/success')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      setIsSubmitting(false)
      setSubmitStatus(null)
    }
  }

  // Step indices:
  // email=0, guidelines=1, name=2, grade=3, photo=4, peopleCheck=5, title=6, review=7
  const titleStepIndex = 6
  const photoStepIndex = 4

  const reviewItems = [
    { label: 'Name', value: formData.name, step: 2 },
    { label: 'Email', value: formData.email, step: 0 },
    { label: 'Grade', value: formData.grade, step: 3 },
    { label: 'Title', value: formData.photoTitle, step: titleStepIndex },
  ]

  // Determine if we can submit
  const canSubmit = !hasAnyUploading && !hasAnyError

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 text-center">
        <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">Ready to submit?</h2>
        <p className="text-foreground-muted text-lg">Review your submission before sending</p>
      </motion.div>

      {/* Photo preview */}
      {formData.photoPreview && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-surface-hover relative mx-auto max-w-sm overflow-hidden rounded-2xl shadow-lg"
        >
          <img
            src={formData.photoPreview}
            alt={formData.photoTitle}
            className="mx-auto max-h-[300px] w-full object-contain"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute right-4 bottom-4 left-4">
            <p className="text-lg font-semibold text-white">{formData.photoTitle}</p>
          </div>
          <button
            type="button"
            onClick={() => goToStep(photoStepIndex)}
            className="absolute top-3 right-3 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </motion.div>
      )}

      {/* Review details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-surface space-y-4 rounded-2xl p-6"
      >
        {reviewItems.map((item) => (
          <div key={item.label} className="flex items-center justify-between">
            <div>
              <p className="text-foreground-subtle text-sm">{item.label}</p>
              <p className="text-foreground font-medium">{item.value}</p>
            </div>
            <button
              type="button"
              onClick={() => goToStep(item.step)}
              className="text-foreground-muted hover:text-foreground hover:bg-surface-hover rounded-lg p-2 transition-colors"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        ))}
      </motion.div>

      {/* Upload status indicator for photo */}
      {photoUpload && photoUpload.status !== 'idle' && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <div className="border-border bg-surface rounded-xl border p-3">
            {photoUpload.status === 'uploading' && (
              <div className="flex items-center gap-3">
                <Loader2 className="text-accent h-5 w-5 animate-spin" />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground text-sm font-medium">Uploading photo...</p>
                  <div className="bg-surface-hover mt-1.5 h-1.5 overflow-hidden rounded-full">
                    <motion.div
                      className="bg-accent h-full rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${photoUpload.progress}%` }}
                      transition={{ duration: 0.2 }}
                    />
                  </div>
                </div>
                <span className="text-foreground-muted text-sm font-medium">{photoUpload.progress}%</span>
              </div>
            )}

            {photoUpload.status === 'success' && (
              <div className="flex items-center gap-3">
                <CheckCircle className="text-success h-5 w-5" />
                <p className="text-success text-sm font-medium">Photo uploaded successfully</p>
              </div>
            )}

            {photoUpload.status === 'error' && (
              <div className="flex items-center gap-3">
                <AlertCircle className="text-error h-5 w-5" />
                <div className="flex-1">
                  <p className="text-error text-sm font-medium">Upload failed</p>
                  <p className="text-foreground-muted text-xs">{photoUpload.error}</p>
                </div>
                {onRetryPhotoUpload && (
                  <button
                    onClick={onRetryPhotoUpload}
                    className="bg-accent text-accent-foreground hover:bg-accent/90 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-error/10 border-error/20 rounded-xl border p-4"
        >
          <p className="text-error text-sm">{error}</p>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button
          size="xl"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting || !canSubmit}
          className="flex-1"
        >
          <Send className="h-5 w-5" />
          {submitStatus || 'Submit Photo'}
        </Button>
      </motion.div>
    </div>
  )
}
